import React, { useState, useEffect } from 'react';
import { useAuthUnified, useAuthDemo } from '@/domains/billing/hooks/shared/useAuthUnified';
import { useNotifications } from '@/domains/billing/hooks/shared/useNotifications';
import { NotificationBell } from '@/shared/components/notifications/NotificationBell';
import { NotificationDropdown } from '@/shared/components/notifications/NotificationDropdown';
import { SearchPalette } from '@/shared/components/search/SearchPalette';
import { useCommentMode } from '@/shared/contexts/CommentModeProvider';
import { User, LogOut, Menu, ChevronUp, ChevronDown, MessageSquare, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Z_INDEX } from '@/lib/z-index';
import DemoRoleToggle from '@/domains/admin/components/auth/DemoRoleToggle';

interface FloatingControlsProps {
  onMenuToggle?: () => void;  // For mobile hamburger menu
  isMobileMenuOpen?: boolean;
  className?: string;
}

/**
 * Comment Toggle Button Component
 * Positioned between notifications and user menu in FloatingControls
 */
const CommentToggleButton: React.FC = () => {
  const { isCommentModeActive, toggleCommentMode } = useCommentMode();

  return (
    <div className="relative bg-white rounded-xl lg:rounded-xl rounded-lg shadow-lg border border-gray-200 p-2">
      <button
        onClick={toggleCommentMode}
        className={cn(
          "p-2 rounded-md transition-colors relative",
          isCommentModeActive
            ? "bg-purple-100 text-purple-700 hover:bg-purple-200"
            : "text-gray-600 hover:text-purple-700 hover:bg-purple-50"
        )}
        title={isCommentModeActive ? "Click anywhere to add comment (ESC to cancel)" : "Add comment"}
        aria-label={isCommentModeActive ? "Cancel comment mode" : "Activate comment mode"}
      >
        <MessageSquare className="h-5 w-5" />
        {isCommentModeActive && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 rounded-full animate-pulse" />
        )}
      </button>
    </div>
  );
};

/**
 * FloatingControls Component
 * 
 * Floating control bar that provides quick access to key features:
 * - Admin access button (for admin users)
 * - Notification bell with dropdown
 * - User profile menu with sign out
 * - Mobile hamburger menu toggle
 * 
 * Layout:
 * - Desktop (lg+): Fixed top-right corner, horizontal layout
 * - Mobile (<lg): Fixed bottom bar, full width
 */
export const FloatingControls: React.FC<FloatingControlsProps> = ({
  onMenuToggle,
  isMobileMenuOpen,
  className
}) => {
  const { user, effectiveUser, signOut, isDemoMode } = useAuthUnified();
  const { isDevEnvironment } = useAuthDemo();
  const { unreadCount } = useNotifications();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false);
  const [isDemoCollapsed, setIsDemoCollapsed] = useState(true);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleNotificationToggle = (isOpen: boolean) => {
    setIsNotificationDropdownOpen(isOpen);
    // Close user menu if notification dropdown is opened
    if (isOpen) {
      setIsUserMenuOpen(false);
    }
  };

  const handleNotificationClose = () => {
    setIsNotificationDropdownOpen(false);
  };

  const handleUserMenuToggle = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
    // Close notification dropdown if user menu is opened
    if (!isUserMenuOpen) {
      setIsNotificationDropdownOpen(false);
    }
  };

  const handleSearchOpen = () => {
    setIsSearchOpen(true);
    setIsUserMenuOpen(false);
    setIsNotificationDropdownOpen(false);
  };

  // Global "/" keyboard shortcut to open search palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.key === '/' &&
        !['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement).tagName)
      ) {
        e.preventDefault();
        handleSearchOpen();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {/* Desktop Layout - Top Right Corner - Separated Containers */}
      <div
        className={cn(
          "hidden lg:flex items-center gap-3",
          "fixed top-4 right-8",
          className
        )}
        style={{ zIndex: Z_INDEX.FLOATING_CONTROLS }}
      >
        {/* Search Button */}
        <div className="relative bg-white rounded-xl shadow-lg border border-gray-200 p-2">
          <button
            onClick={handleSearchOpen}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            title="Search (press /)"
            aria-label="Open search"
          >
            <Search className="h-5 w-5" />
          </button>
        </div>

        {/* Notification Bell */}
        <div className="relative bg-white rounded-xl shadow-lg border border-gray-200 p-2">
          <NotificationBell
            unreadCount={unreadCount}
            onToggle={handleNotificationToggle}
            isOpen={isNotificationDropdownOpen}
          />
        </div>

        {/* Comment Toggle Button */}
        <CommentToggleButton />

        {/* User Profile - Avatar Only */}
        <div className="relative bg-white rounded-xl shadow-lg border border-gray-200 p-2">
          <button
            onClick={handleUserMenuToggle}
            className="flex items-center p-1 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
            aria-expanded={isUserMenuOpen}
            aria-haspopup="true"
            aria-label="User menu"
          >
            {/* Use effectiveUser cached avatar in demo mode, fallback to Firebase user photo */}
            {(isDemoMode ? effectiveUser?.cached_avatar_url : user?.photoURL) ? (
              <img
                src={isDemoMode ? (effectiveUser?.cached_avatar_url || '') : (user?.photoURL || '')}
                alt="Profile"
                className="h-8 w-8 rounded-full"
              />
            ) : (
              <div className="h-8 w-8 bg-gray-300 rounded-full flex items-center justify-center">
                <User className="h-4 w-4 text-gray-600" />
              </div>
            )}
          </button>

          {/* User dropdown menu */}
          {isUserMenuOpen && (
            <div
              className="absolute right-0 mt-2 w-72 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5"
              style={{ zIndex: Z_INDEX.FLOATING_DROPDOWNS }}
            >
              <div className="py-1">
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-medium text-gray-900">
                    {effectiveUser?.displayName || effectiveUser?.name || user?.displayName}
                  </p>
                  <p className="text-xs text-gray-500">{user?.email}</p>
                  <p className="text-xs text-gray-500 capitalize mt-1">
                    Role: {effectiveUser?.role}
                  </p>
                </div>

                {/* Demo Mode Section - Only in Development */}
                {isDevEnvironment && (
                  <div className="px-4 py-3 border-b border-gray-100">
                    <button
                      onClick={() => setIsDemoCollapsed(!isDemoCollapsed)}
                      className="w-full flex items-center justify-between text-left"
                    >
                      <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded-full border border-orange-200">
                        🚧 DEMO MODE
                      </span>
                      {isDemoCollapsed ? (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronUp className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                    {!isDemoCollapsed && (
                      <div className="mt-3">
                        <DemoRoleToggle />
                      </div>
                    )}
                  </div>
                )}
                
                <button
                  onClick={handleSignOut}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign Out</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Mobile Layout - Bottom Bar with Separated Containers */}
      <div
        className={cn(
          "lg:hidden fixed bottom-0 left-0 right-0",
          "bg-transparent",
          "px-4 py-3",
          className
        )}
        style={{ zIndex: Z_INDEX.FLOATING_CONTROLS }}
      >
        <div className="flex items-center justify-between gap-3 max-w-screen-xl mx-auto">
          {/* Mobile Menu Toggle */}
          {onMenuToggle && (
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-2">
              <button
                onClick={onMenuToggle}
                className={cn(
                  "p-2 rounded-md transition-colors",
                  isMobileMenuOpen
                    ? "bg-purple-50 text-purple-700"
                    : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                )}
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
              >
                <Menu className="h-6 w-6" />
              </button>
            </div>
          )}

          <div className="flex items-center gap-3">
            {/* Search Button */}
            <div className="relative bg-white rounded-lg shadow-lg border border-gray-200 p-2">
              <button
                onClick={handleSearchOpen}
                className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                title="Search"
                aria-label="Open search"
              >
                <Search className="h-5 w-5" />
              </button>
            </div>

            {/* Notification Bell */}
            <div className="relative bg-white rounded-lg shadow-lg border border-gray-200 p-2">
              <NotificationBell
                unreadCount={unreadCount}
                onToggle={handleNotificationToggle}
                isOpen={isNotificationDropdownOpen}
              />
            </div>

            {/* Comment Toggle Button */}
            <CommentToggleButton />

            {/* User Profile - Avatar Only */}
            <div className="relative bg-white rounded-lg shadow-lg border border-gray-200 p-2">
              <button
                onClick={handleUserMenuToggle}
                className="flex items-center p-1 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                aria-expanded={isUserMenuOpen}
                aria-haspopup="true"
                aria-label="User menu"
              >
                {/* Use effectiveUser cached avatar in demo mode, fallback to Firebase user photo */}
                {(isDemoMode ? effectiveUser?.cached_avatar_url : user?.photoURL) ? (
                  <img
                    src={isDemoMode ? (effectiveUser?.cached_avatar_url || '') : (user?.photoURL || '')}
                    alt="Profile"
                    className="h-8 w-8 rounded-full"
                  />
                ) : (
                  <div className="h-8 w-8 bg-gray-300 rounded-full flex items-center justify-center">
                    <User className="h-5 w-5 text-gray-600" />
                  </div>
                )}
              </button>

              {/* User dropdown menu */}
              {isUserMenuOpen && (
                <div
                  className="absolute right-0 bottom-full mb-2 w-72 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5"
                  style={{ zIndex: Z_INDEX.FLOATING_DROPDOWNS }}
                >
                  <div className="py-1">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">
                        {effectiveUser?.displayName || effectiveUser?.name || user?.displayName}
                      </p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                      <p className="text-xs text-gray-500 capitalize mt-1">
                        Role: {effectiveUser?.role}
                      </p>
                    </div>

                    {/* Demo Mode Section - Only in Development */}
                    {isDevEnvironment && (
                      <div className="px-4 py-3 border-b border-gray-100">
                        <button
                          onClick={() => setIsDemoCollapsed(!isDemoCollapsed)}
                          className="w-full flex items-center justify-between text-left"
                        >
                          <span className="text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded-full border border-orange-200">
                            🚧 DEMO MODE
                          </span>
                          {isDemoCollapsed ? (
                            <ChevronDown className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronUp className="h-4 w-4 text-gray-400" />
                          )}
                        </button>
                        {!isDemoCollapsed && (
                          <div className="mt-3">
                            <DemoRoleToggle />
                          </div>
                        )}
                      </div>
                    )}
                    
                    <button
                      onClick={handleSignOut}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center space-x-2"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Shared Notification Dropdown - Rendered once, positioned based on screen size */}
      {isNotificationDropdownOpen && (
        <div
          className={cn(
            "fixed",
            // Desktop: position near top-right notification bell
            "lg:top-20 lg:right-8",
            // Mobile: position near bottom notification bell
            "bottom-20 right-4 lg:bottom-auto lg:left-auto"
          )}
          style={{ zIndex: Z_INDEX.FLOATING_DROPDOWNS }}
        >
          <NotificationDropdown
            isOpen={isNotificationDropdownOpen}
            onClose={handleNotificationClose}
            onNavigate={(notification) => {
              console.log('Navigate to notification:', notification);
              handleNotificationClose();
              
              // Check if this is a cross-domain comment mention (has pin context)
              if (notification.mentionData?.pageUrl && notification.mentionData?.pinId) {
                // Cross-domain comment deep link
                const url = `${notification.mentionData.pageUrl}?comment=${notification.mentionData.pinId}&scroll=${notification.mentionData.scrollY || 0}`;
                window.location.href = url; // Use full page navigation to ensure proper page load
              } else if (notification.mentionData?.workflowId) {
                // Billing invoice chat deep link
                window.location.href = `/billing/team-tasks/${notification.mentionData.workflowId}`;
              }
            }}
          />
        </div>
      )}

      {/* Click outside to close dropdowns */}
      {(isUserMenuOpen || isNotificationDropdownOpen) && (
        <div
          className="fixed inset-0"
          style={{ zIndex: Z_INDEX.FLOATING_CONTROLS - 1 }}
          onClick={() => {
            setIsUserMenuOpen(false);
            setIsNotificationDropdownOpen(false);
          }}
        />
      )}

      {/* Global Search Palette */}
      <SearchPalette isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};

